import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send, Loader2, User, Phone, Mail, Briefcase, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PropertyMessaging = ({ propertyId, currentUser, contactInfo }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendLoading, setSendLoading] = useState(false);
  const [text, setText] = useState('');
  const [showRepFields, setShowRepFields] = useState(false);
  const [repName, setRepName] = useState('');
  const [repRole, setRepRole] = useState('');
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [fetchedContact, setFetchedContact] = useState(contactInfo || null);
  const messagesEndRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messages/${propertyId}`);
      setMessages(res.data.messages || []);
      if (res.data.contact_info) {
        setFetchedContact(res.data.contact_info);
      }
    } catch (error) {
      logger.error('Failed to poll messages:', error);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [propertyId, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    // Scroll only the messages container (not the whole page).
    const el = messagesEndRef.current;
    if (!el) return;
    const scrollParent = el.parentElement;
    if (scrollParent) {
      scrollParent.scrollTop = scrollParent.scrollHeight;
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSendLoading(true);
    try {
      const payload = { text: text.trim() };
      if (showRepFields && repName.trim()) {
        payload.representative_name = repName.trim();
        payload.representative_role = repRole.trim() || 'Representative';
      }
      await axios.post(`${API_URL}/api/messages/${propertyId}`, payload);
      setText('');
      setRepName('');
      setRepRole('');
      setShowRepFields(false);
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendLoading(false);
    }
  };

  const formatTime = (isoStr) => {
    try {
      const d = new Date(isoStr);
      return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const contact = fetchedContact || contactInfo;

  return (
    <div className="luxury-card overflow-hidden" data-testid="property-messaging">
      {/* Header */}
      <div className="p-4 bg-[#0F172A] text-white flex items-center justify-between">
        <h3 className="font-['Playfair_Display'] text-lg font-semibold tracking-wide">Communication</h3>
        {contact && (
          <button
            onClick={() => setShowContactInfo(!showContactInfo)}
            className="flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            data-testid="toggle-contact-info"
          >
            <Phone className="h-3.5 w-3.5" />
            Contact Info
            {showContactInfo ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>

      {/* Contact Info Panel */}
      {showContactInfo && contact && (
        <div className="p-4 bg-[#FEF3EC] border-b border-[#E07A3A]/10" data-testid="contact-info-panel">
          {contact.seller_name && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-[#1A1A1A]">Seller Contact:</p>
              <div className="flex items-center gap-2 text-sm text-[#1A1A1A]/80">
                <User className="h-3.5 w-3.5 text-[#E07A3A]" /> {contact.seller_name}
              </div>
              {contact.seller_email && (
                <div className="flex items-center gap-2 text-sm text-[#1A1A1A]/80">
                  <Mail className="h-3.5 w-3.5 text-[#E07A3A]" />
                  <a href={`mailto:${contact.seller_email}`} className="text-[#E07A3A] hover:underline">{contact.seller_email}</a>
                </div>
              )}
              {contact.seller_phone && (
                <div className="flex items-center gap-2 text-sm text-[#1A1A1A]/80">
                  <Phone className="h-3.5 w-3.5 text-[#E07A3A]" />
                  <a href={`tel:${contact.seller_phone}`} className="text-[#E07A3A] hover:underline">{contact.seller_phone}</a>
                </div>
              )}
            </div>
          )}
          {contact.approved_buyers && contact.approved_buyers.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-[#1A1A1A]">Approved Buyers:</p>
              {contact.approved_buyers.map((b, i) => (
                <div key={`buyer-${i}`} className="p-2 bg-white border border-slate-200 space-y-1">
                  <div className="flex items-center gap-2 text-sm text-[#1A1A1A]/80">
                    <User className="h-3.5 w-3.5 text-[#E07A3A]" /> {b.name}
                  </div>
                  {b.email && (
                    <div className="flex items-center gap-2 text-sm text-[#1A1A1A]/80">
                      <Mail className="h-3.5 w-3.5 text-[#E07A3A]" />
                      <a href={`mailto:${b.email}`} className="text-[#E07A3A] hover:underline">{b.email}</a>
                    </div>
                  )}
                  {b.phone && (
                    <div className="flex items-center gap-2 text-sm text-[#1A1A1A]/80">
                      <Phone className="h-3.5 w-3.5 text-[#E07A3A]" />
                      <a href={`tel:${b.phone}`} className="text-[#E07A3A] hover:underline">{b.phone}</a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="h-72 overflow-y-auto p-4 space-y-3 bg-[#F8FAFC]" data-testid="messages-list">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 text-[#E07A3A] animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">
            No messages yet. Start the conversation below.
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_id === currentUser?.id;
            return (
              <div
                key={msg.id}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] ${isMe ? 'bg-[#E07A3A] text-white' : 'bg-white border border-slate-200 text-[#1A1A1A]'} p-3 shadow-sm`}>
                  {!isMe && (
                    <p className={`text-xs font-semibold mb-1 ${isMe ? 'text-white/80' : 'text-[#E07A3A]'}`}>
                      {msg.sender_name} ({msg.sender_role})
                    </p>
                  )}
                  {msg.representative_name && (
                    <div className={`flex items-center gap-1 text-xs mb-1 ${isMe ? 'text-white/70' : 'text-slate-500'}`}>
                      <Briefcase className="h-3 w-3" />
                      via {msg.representative_name} ({msg.representative_role || 'Representative'})
                    </div>
                  )}
                  <p className="text-sm leading-relaxed">{msg.text}</p>
                  <p className={`text-xs mt-1 ${isMe ? 'text-white/60' : 'text-slate-400'}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Send Message */}
      <div className="p-4 border-t border-slate-200 bg-white">
        {/* Representative toggle */}
        <button
          type="button"
          onClick={() => setShowRepFields(!showRepFields)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#E07A3A] mb-2 transition-colors"
          data-testid="toggle-representative"
        >
          <Briefcase className="h-3 w-3" />
          {showRepFields ? 'Hide' : 'Send via'} Professional Representative
        </button>

        {showRepFields && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <Label className="text-xs text-slate-500">Representative Name</Label>
              <Input
                value={repName}
                onChange={(e) => setRepName(e.target.value)}
                placeholder="e.g., John Smith"
                className="h-8 text-sm bg-[#F8FAFC] border-slate-200"
                data-testid="rep-name-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Role / Title</Label>
              <Input
                value={repRole}
                onChange={(e) => setRepRole(e.target.value)}
                placeholder="e.g., Solicitor"
                className="h-8 text-sm bg-[#F8FAFC] border-slate-200"
                data-testid="rep-role-input"
              />
            </div>
          </div>
        )}

        <form onSubmit={handleSend} className="flex gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] min-h-[44px] max-h-24 resize-none text-sm"
            rows={1}
            data-testid="message-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
          />
          <Button
            type="submit"
            disabled={sendLoading || !text.trim()}
            className="bg-[#E07A3A] text-white hover:bg-[#C4652E] h-11 w-11 p-0 flex-shrink-0"
            data-testid="send-message-btn"
          >
            {sendLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PropertyMessaging;
